import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useAccountId } from '../../../../redux/slices/appInfo/hooks';
import { useGetPromoQuotaQuery } from '../../../../redux/api/fymAPI';
import { rolloutMessages } from '../releases/plan/rollout/rolloutMessages';
import { PostTimeField } from './PostTimeField';
import type { PostStateActionsProps } from './dayPanelTypes';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the panel; each part uses its own slice of it */
import styles from './DayPanel.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * A draft (design b3): it never goes out. "Schedule" arms it at its own time and takes one
 * scheduled post. "Schedule all N drafts of this day" schedules at once, or asks first for the
 * answers that a platform needs (`useCalendarLaunch`); the line under it says the units. A draft
 * moves with no cost, and it can be deleted.
 */
const DraftActionsComponent: React.FC<PostStateActionsProps> = ({
  post,
  dayDrafts,
  actions,
  onEditInComposer,
  onScheduleDrafts,
  onAskDelete,
}) => {
  const intl = useIntl();
  const accountId = useAccountId();
  const { data: quota } = useGetPromoQuotaQuery({ accountId: accountId ?? 0 }, { skip: accountId === null });
  const [isTimeOpen, setTimeOpen] = useState(false);
  const limit = quota?.scheduledPost.limit ?? null;
  const left = limit === null || quota === undefined ? null : Math.max(limit - quota.scheduledPost.used, 0);

  const schedule = useCallback(() => {
    actions.schedule(post.uuid).catch(console.error);
  }, [actions, post.uuid]);
  const move = useCallback(
    (at: Date) => {
      actions
        .move(post.uuid, at)
        .then((result) => (result.ok ? setTimeOpen(false) : undefined))
        .catch(console.error);
    },
    [actions, post.uuid]
  );

  return (
    <>
      <div className={styles.dpBody}>
        <p className={styles.why}>
          {intl.formatMessage({
            id: 'app.sml.promote.calendar.draft.why',
            defaultMessage: 'A draft never goes out. Schedule it to send it at its time.',
          })}
        </p>
        {post.needsSettings ? (
          <p className={styles.why} data-testid="needs-settings">
            <b>
              {intl.formatMessage({
                id: 'app.sml.promote.calendar.needsSettings.title',
                defaultMessage: 'Needs your settings',
              })}
            </b>{' '}
            {intl.formatMessage({
              id: 'app.sml.promote.calendar.needsSettings.body',
              defaultMessage: 'Choose who can see it and the other settings before it can go out.',
            })}
          </p>
        ) : null}
        {left === null || post.needsSettings ? null : (
          <span className={styles.quota} data-testid="draft-quota">
            {intl.formatMessage(
              {
                id: 'app.sml.promote.calendar.draft.quota',
                defaultMessage: 'Scheduling takes 1 of the {left} scheduled posts left this month.',
              },
              { left }
            )}
          </span>
        )}
        {isTimeOpen ? (
          <PostTimeField
            scheduledAt={post.scheduledAt}
            label={intl.formatMessage({ id: 'app.sml.promote.calendar.time.label', defaultMessage: 'Time' })}
            help={intl.formatMessage({
              id: 'app.sml.promote.calendar.draft.timeHelp',
              defaultMessage: 'The post stays a draft. A new time takes no scheduled post.',
            })}
            isSaving={actions.isWriting}
            onSave={move}
          />
        ) : null}
      </div>
      <div className={styles.dpActs}>
        {post.needsSettings ? (
          <SMLButton color="primary" omitCap stretch onClick={() => onEditInComposer(post)}>
            {intl.formatMessage({
              id: 'app.sml.promote.calendar.needsSettings.choose',
              defaultMessage: 'Choose the settings',
            })}
          </SMLButton>
        ) : (
          <SMLButton color="primary" omitCap stretch disabled={actions.isWriting} onClick={schedule}>
            {intl.formatMessage({ id: 'app.sml.promote.calendar.draft.schedule', defaultMessage: 'Schedule' })}
          </SMLButton>
        )}
        {dayDrafts.length > 1 ? (
          <>
            <SMLButton size="sm" outline omitCap stretch onClick={() => onScheduleDrafts(dayDrafts)}>
              {intl.formatMessage(
                {
                  id: 'app.sml.promote.calendar.draft.scheduleAll',
                  defaultMessage: 'Schedule all {count} drafts of this day',
                },
                { count: dayDrafts.length }
              )}
            </SMLButton>
            {/* The units of the click, as the plan says them next to "Schedule all N posts". */}
            <span className={styles.quota} data-testid="schedule-all-cost">
              {left !== null && dayDrafts.length > left
                ? intl.formatMessage(rolloutMessages.scheduleAllFit, { fits: left, left: dayDrafts.length - left })
                : intl.formatMessage(rolloutMessages.scheduleAllUses, { count: dayDrafts.length })}
            </span>
          </>
        ) : null}
        <div className={styles.row2}>
          <SMLButton size="sm" outline omitCap onClick={() => onEditInComposer(post)}>
            {intl.formatMessage({ id: 'app.sml.promote.calendar.editInComposer', defaultMessage: 'Edit in Composer' })}
          </SMLButton>
          <SMLButton size="sm" outline omitCap onClick={() => setTimeOpen((current) => !current)}>
            {intl.formatMessage({ id: 'app.sml.promote.calendar.changeTime', defaultMessage: 'Change the time' })}
          </SMLButton>
        </div>
        <button type="button" className={styles.linkbtn} onClick={() => onAskDelete(post)}>
          {intl.formatMessage({ id: 'app.sml.promote.calendar.draft.delete', defaultMessage: 'Delete this draft' })}
        </button>
      </div>
    </>
  );
};

export const DraftActions = TypedMemo(DraftActionsComponent);
